import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { FabButton, RouteError } from "@steel-cut/steel-lib";
import { useListRead } from "@/hooks/list/useListRead";
import { useListReorder } from "@/hooks/list/useListReorder";
import { trpc } from "@/trpc";
import { SortableListsTable } from "@/features/lists/SortableListsTable";

export const Route = createFileRoute("/_authenticated/lists/")({
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(trpc.list.getAll.queryOptions(undefined));
    return {};
  },

  pendingComponent: () => (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex items-center justify-between">
        <div className="h-9 w-64 animate-pulse rounded bg-muted" />
        <div className="h-6 w-24 animate-pulse rounded bg-muted" />
      </div>

      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-[52px] animate-pulse rounded-md bg-muted/70 border"
          />
        ))}
      </div>
    </div>
  ),

  pendingMs: 0,
  pendingMinMs: 400,

  errorComponent: ({ error, reset }) => (
    <RouteError
      error={error}
      reset={reset}
      title="Failed to load your lists"
      backLabel="Retry"
    />
  ),

  component: ListsPage,
});

function ListsPage() {
  const navigate = useNavigate();

  const { lists } = useListRead();

  const { pendingReorder, updateListOrder, isReordering } = useListReorder();

  const displayedLists = pendingReorder ?? lists;

  const listCount = displayedLists.length;

  const handleCreateNew = () => {
    navigate({ to: "/lists/new" });
  };

  if (listCount === 0) {
    return (
      <>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 text-center">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Your Lists
            </h1>
            <p className="mt-3 text-muted-foreground">
              You don't have any lists yet.
            </p>
          </div>

          <FabButton
            onClick={handleCreateNew}
            label="Create your first list"
            testId="fab-add-list"
            pulse={true}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6 sm:space-y-8 pb-20 md:pb-24">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Your Lists
          </h1>
          <div className="text-sm font-medium text-muted-foreground">
            {listCount} {listCount === 1 ? "list" : "lists"}
          </div>
        </div>

        <SortableListsTable
          lists={displayedLists}
          updateListOrder={updateListOrder}
          isReordering={isReordering}
        />
      </div>

      <FabButton
        onClick={handleCreateNew}
        label="Create new list"
        testId="fab-add-list"
        pulse={listCount < 1}
      />
    </>
  );
}
